import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";
import { useCart } from "../lib/cart";

// Each category has a list of distinct products. Customer reads what's inside.
const CATALOG = {
  giftbox: {
    products: [
      {
        id: "essentials",
        name: "Essentials Καλάθι",
        image: "/gifts/essentials.jpg",
        story: "Παραδοσιακό καλάθι με τα τρία κλασικά της Ελλάδας — διακριτικό αυθεντικό δώρο.",
        contents: ["Extra Virgin Ελαιόλαδο 250ml", "Θυμαρίσιο Μέλι 200g", "Σαπούνι Ελαιόλαδου"],
        size: "3 προϊόντα",
        price: 18,
        tier: "small",
      },
      {
        id: "heritage",
        name: "Heritage Κουτί",
        image: "/gifts/heritage.jpg",
        story: "Μεσαίο πολυτελές κουτί με πέντε προσεκτικά επιλεγμένα προϊόντα. Ιδανικό για ξενοδοχεία και εταιρικά δώρα.",
        contents: ["Extra Virgin Ελαιόλαδο 500ml", "Θυμαρίσιο Μέλι 380g", "Παραδοσιακό Τσίπουρο 200ml", "Ελιές Καλαμών 200g", "Σαπούνι Ελαιόλαδου"],
        size: "5 προϊόντα",
        price: 45,
        tier: "medium",
      },
      {
        id: "premium",
        name: "Premium Gift Box",
        image: "/gifts/premium.jpg",
        story: "Πολυτελές μαύρο κουτί με χρυσή κορδέλα. Έξι εκλεκτά ελληνικά προϊόντα — η πιο ολοκληρωμένη εμπειρία.",
        contents: ["Extra Virgin Ελαιόλαδο 750ml", "Santorini Assyrtiko 2022 — 750ml", "Θυμαρίσιο Μέλι 380g", "Ρακόμελο 750ml", "Τσίπουρο 750ml", "Σαπούνι Ελαιόλαδου"],
        size: "6 προϊόντα",
        price: 100,
        tier: "premium",
      },
    ],
  },
  oil: {
    products: [
      {
        id: "goldenelia-250",
        name: "Golden Elia Extra Virgin",
        image: "/products/oil-250.jpg",
        story: "Από τους ελαιώνες της Κορώνης. Ψυχρή έκθλιψη μέσα σε 4 ώρες από τη συλλογή, οξύτητα 0.3%.",
        contents: ["Κορωνέικη ποικιλία 100%", "Συγκομιδή Νοέμβριος", "Αρωματικές νότες θυμαριού & φρέσκου χόρτου"],
        size: "250ml",
        price: 12,
        tier: "small",
      },
      {
        id: "goldenelia-500",
        name: "Golden Elia Extra Virgin",
        image: "/products/oil-500.jpg",
        story: "Ο γενναιόδωρος μεσαίος κωδικός μας — το αγαπημένο των σεφ. Για καθημερινή χρήση και φινίρισμα πιάτων.",
        contents: ["Κορωνέικη ποικιλία 100%", "Πιστοποίηση ΠΟΠ", "Σκούρο γυάλινο μπουκάλι"],
        size: "500ml",
        price: 22,
        tier: "medium",
      },
      {
        id: "goldenelia-1l",
        name: "Golden Elia Reserve 1L",
        image: "/products/oil-1l.jpg",
        story: "Premium φιάλη 1L — η οικογενειακή επιλογή ή ιδανικό δώρο σε αγαπημένο πρόσωπο.",
        contents: ["Κορωνέικη ποικιλία 100%", "Limited edition συσκευασία", "Numbered batch certificate"],
        size: "1L",
        price: 38,
        tier: "premium",
      },
    ],
  },
  olives: {
    products: [
      {
        id: "kalamon-200",
        name: "Καλαμών Premium",
        image: "/products/olives-200.jpg",
        story: "Αυθεντικές ελιές Καλαμών σε ελαιόλαδο. Χωρίς συντηρητικά, με βαθιά γεύση και τραγανή σάρκα.",
        contents: ["Ελιές Καλαμών 200g", "Extra virgin ελαιόλαδο", "Χωρίς συντηρητικά"],
        size: "200g",
        price: 8,
        tier: "small",
      },
      {
        id: "throumbes-350",
        name: "Θρούμπες Θάσου",
        image: "/products/olives-350.jpg",
        story: "Φυσικά ωριμασμένες ελιές Θάσου με χονδρό θαλασσινό αλάτι. Συγκλονιστική γεύση χωρίς μαρινάδα.",
        contents: ["Θρούμπες Θάσου 350g", "Φυσική ωρίμανση", "Θαλασσινό αλάτι"],
        size: "350g",
        price: 13,
        tier: "medium",
      },
      {
        id: "chalkidiki-500",
        name: "Πράσινες Χαλκιδικής",
        image: "/products/olives-500.jpg",
        story: "Μεγαλόκαρπες πράσινες ελιές Χαλκιδικής, γεμιστές με αμύγδαλο. Premium meze επιλογή.",
        contents: ["Πράσινες ελιές Χαλκιδικής 500g", "Γεμιστές με αμύγδαλο", "Σε άλμη"],
        size: "500g",
        price: 18,
        tier: "premium",
      },
    ],
  },
  honey: {
    products: [
      {
        id: "thyme-250",
        name: "Θυμαρίσιο Μέλι",
        image: "/products/honey-250.jpg",
        story: "100% θυμαρίσιο μέλι από τα Κρητικά βουνά. Συλλέγεται τέλη Ιουλίου από άγριο θυμάρι.",
        contents: ["100% θυμαρίσιο", "Raw, χωρίς θέρμανση", "Από μικρή μελισσοκομία"],
        size: "250g",
        price: 11,
        tier: "small",
      },
      {
        id: "wildflower-450",
        name: "Ανθέων Premium",
        image: "/products/honey-450.jpg",
        story: "Πολυανθικό μέλι από ορεινά λιβάδια της Πελοποννήσου. Πιο ήπιο και αρωματικό από το θυμαρίσιο.",
        contents: ["Άγρια λιβάδια", "Πολυανθικό", "Με κερί κηρήθρας στο πάτο"],
        size: "450g",
        price: 18,
        tier: "medium",
      },
      {
        id: "honeycomb-800",
        name: "Μέλι με Κηρήθρα",
        image: "/products/honey-800.jpg",
        story: "Το πιο πολυτελές μας μέλι — με ολόκληρο κομμάτι αυθεντικής κηρήθρας μέσα στο βάζο.",
        contents: ["Θυμαρίσιο μέλι 800g", "Κομμάτι αυθεντικής κηρήθρας", "Premium γυάλινο βάζο"],
        size: "800g",
        price: 30,
        tier: "premium",
      },
    ],
  },
  raki: {
    products: [
      {
        id: "tsikoudia-350",
        name: "Golden Elia Cretan Tsikoudia",
        image: "/products/raki-350.jpg",
        story: "Παραδοσιακή κρητική τσικουδιά, διπλή απόσταξη από επιλεγμένα στέμφυλα. 42% vol.",
        contents: ["Διπλή απόσταξη", "42% vol", "Φυσική απόσταξη χωρίς πρόσθετα"],
        size: "350ml",
        price: 15,
        tier: "small",
      },
      {
        id: "rakomelo-500",
        name: "Ρακόμελο Golden Elia",
        image: "/products/raki-500.jpg",
        story: "Τσικουδιά αρωματισμένη με θυμαρίσιο μέλι και κρητικά μπαχαρικά. Σερβίρεται ζεστό χειμώνα.",
        contents: ["Κρητική τσικουδιά", "Θυμαρίσιο μέλι", "Κανέλα, γαρίφαλο, μοσχοκάρυδο"],
        size: "500ml",
        price: 22,
        tier: "medium",
      },
      {
        id: "raki-aged-700",
        name: "Old Vine Raki Reserve",
        image: "/products/raki-700.jpg",
        story: "Παλαιωμένη ρακή 5 ετών σε δρύινα βαρέλια. Βαθύ ξανθό χρώμα, νότες βανίλιας και μελιού.",
        contents: ["Παλαίωση 5 ετών", "Σε δρύινα βαρέλια", "Premium φιάλη με μαύρο κερί"],
        size: "700ml",
        price: 30,
        tier: "premium",
      },
    ],
  },
  wine: {
    products: [
      {
        id: "assyrtiko-375",
        name: "Santorini Assyrtiko",
        image: "/products/wine-375.jpg",
        story: "PDO Σαντορίνη Ασύρτικο 2022. Ορυκτή έκφραση από τα ηφαιστειακά εδάφη, υψηλή οξύτητα.",
        contents: ["100% Ασύρτικο", "PDO Σαντορίνη", "Λευκό ξηρό · 13.5% vol"],
        size: "375ml",
        price: 15,
        tier: "small",
      },
      {
        id: "vidiano-750",
        name: "Vidiano Cretan White",
        image: "/products/wine-750.jpg",
        story: "Κρητικό Βιδιανό από τους αμπελώνες των Δαφνών. Αρωματικό λευκό με νότες μήλου και ροδάκινου.",
        contents: ["100% Βιδιανό", "Κρήτη — Δαφνές", "Λευκό αρωματικό · 12.5% vol"],
        size: "750ml",
        price: 24,
        tier: "medium",
      },
      {
        id: "liatiko-magnum",
        name: "Λιάτικο Old Vines Magnum",
        image: "/products/wine-magnum.jpg",
        story: "Παλαιωμένο σε δρυς Λιάτικο από αμπέλια 80 ετών της Σητείας. Magnum 1.5L για ξεχωριστές στιγμές.",
        contents: ["100% Λιάτικο", "Παλαίωση 18 μήνες", "Ερυθρό · 14% vol · Magnum 1.5L"],
        size: "Magnum 1.5L",
        price: 45,
        tier: "premium",
      },
    ],
  },
  vinegar: {
    products: [
      {
        id: "balsamic-250",
        name: "Golden Elia Aged Balsamic",
        image: "/products/vinegar-250.jpg",
        story: "12 χρόνια παλαίωση σε ξύλινα βαρέλια. Πυκνό, βελούδινο, με γλυκόξινες νότες σταφυλιού.",
        contents: ["12 ετών παλαίωση", "Πυκνό · DOP-style", "Με dropper καπάκι"],
        size: "250ml",
        price: 9,
        tier: "small",
      },
      {
        id: "apple-500",
        name: "Μηλόξυδο Premium",
        image: "/products/vinegar-500.jpg",
        story: "Από βιολογικά μήλα Έδεσσας, φυσική ζύμωση με μητέρα. Ιδανικό για σαλάτες και ντρέσινγκ.",
        contents: ["Βιολογικά μήλα", "Με μητέρα ξυδιού", "Μη παστεριωμένο"],
        size: "500ml",
        price: 15,
        tier: "medium",
      },
      {
        id: "wine-vinegar-750",
        name: "Ξύδι Κρασιού Reserve",
        image: "/products/vinegar-750.jpg",
        story: "Από Αγιωργίτικο της Νεμέας, παλαιωμένο 6 χρόνια. Πλούσιο σώμα, ιδανικό για κρέατα.",
        contents: ["Από Αγιωργίτικο", "6 ετών παλαίωση", "Νεμέα PDO"],
        size: "750ml",
        price: 22,
        tier: "premium",
      },
    ],
  },
  rusks: {
    products: [
      {
        id: "barley-250",
        name: "Κρητικό Κρίθινο Παξιμάδι",
        image: "/products/rusks-250.jpg",
        story: "Παραδοσιακό κρητικό παξιμάδι από αλεύρι κριθαριού ολικής αλέσεως. Τέλειο για ντάκο.",
        contents: ["Κριθάρι ολικής αλέσεως", "Παραδοσιακή ζύμωση", "Χωρίς συντηρητικά"],
        size: "250g",
        price: 5,
        tier: "small",
      },
      {
        id: "olive-rusk-500",
        name: "Παξιμάδι με Ελιά",
        image: "/products/rusks-500.jpg",
        story: "Κρητικό παξιμάδι με κομμάτια ελιάς Καλαμών μέσα στη ζύμη. Πλήρες snack ή meze.",
        contents: ["Κρίθινο παξιμάδι", "Με ελιές Καλαμών", "Με ρίγανη βουνού"],
        size: "500g",
        price: 9,
        tier: "medium",
      },
      {
        id: "seven-grain-1kg",
        name: "Παξιμάδι 7 Δημητριακών",
        image: "/products/rusks-1kg.jpg",
        story: "Premium μείγμα 7 δημητριακών: σιτάρι, κριθάρι, σίκαλη, βρώμη, φαγόπυρο, καλαμπόκι, κεχρί.",
        contents: ["7 δημητριακά", "Υψηλή θρεπτική αξία", "Vegan & χωρίς συντηρητικά"],
        size: "1kg",
        price: 15,
        tier: "premium",
      },
    ],
  },
  herbs: {
    products: [
      {
        id: "oregano-30",
        name: "Ρίγανη Βουνού",
        image: "/products/herbs-30.jpg",
        story: "Άγρια συλλεκτική ρίγανη από τις πλαγιές του Ολύμπου. Έντονο άρωμα, σκούρο πράσινο χρώμα.",
        contents: ["Άγρια ρίγανη Ολύμπου", "Χωρίς πρόσθετα", "Σε γυάλινο βάζο"],
        size: "30g",
        price: 4,
        tier: "small",
      },
      {
        id: "thyme-50",
        name: "Θυμάρι Κρητικό",
        image: "/products/herbs-50.jpg",
        story: "Κρητικό θυμάρι από τα Λευκά Όρη. Φύλλα ολόκληρα, ξεραμένα φυσικά στον ήλιο.",
        contents: ["Κρητικό θυμάρι", "Φυσικό ξήρανση", "Premium γυάλινο βάζο"],
        size: "50g",
        price: 6,
        tier: "medium",
      },
      {
        id: "salt-100",
        name: "Αλάτι Μεσσηνίας",
        image: "/products/herbs-100.jpg",
        story: "Φυσικό θαλασσινό αλάτι από τις αλυκές της Πύλου. Χωρίς πρόσθετα, με ισορροπημένη γεύση.",
        contents: ["Θαλασσινό αλάτι", "Από αλυκές Πύλου", "Φυσική αποξήρανση"],
        size: "100g",
        price: 11,
        tier: "premium",
      },
    ],
  },
};

export const CATALOG_DATA = CATALOG;

const QtyStepper = ({ value, onChange, testid }) => (
  <div
    className="inline-flex items-center border border-[color:var(--goldenelia-gold)]/40"
    data-testid={`${testid}-stepper`}
  >
    <button
      type="button"
      onClick={() => onChange(Math.max(1, value - 1))}
      className="px-2 py-1.5 text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)]/10 transition-colors"
      data-testid={`${testid}-dec`}
      aria-label="decrease"
    >
      <Minus size={12} strokeWidth={2} />
    </button>
    <span
      className="px-3 text-sm tabular-nums text-[color:var(--goldenelia-cream)] min-w-[2ch] text-center"
      data-testid={`${testid}-value`}
    >
      {value}
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(99, value + 1))}
      className="px-2 py-1.5 text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)]/10 transition-colors"
      data-testid={`${testid}-inc`}
      aria-label="increase"
    >
      <Plus size={12} strokeWidth={2} />
    </button>
  </div>
);

const ProductCard = ({ categoryKey, categoryName, product, onClose }) => {
  const { t } = useLang();
  const { addItem, pulseCart, buyNow } = useCart();
  const [qty, setQty] = useState(1);

  const buildItem = () => ({
    id: `${categoryKey}-${product.id}`,
    categoryKey,
    categoryName,
    productName: product.name,
    size: product.size,
    tier: product.tier,
    unitPrice: product.price,
  });

  const handleAdd = () => {
    addItem(buildItem(), qty);
    pulseCart();
    toast.success(
      `${product.name} · ${product.size} × ${qty}`,
      { description: t("cart.addedToCart") }
    );
    setQty(1);
  };

  const handleBuyNow = () => {
    addItem(buildItem(), qty);
    setQty(1);
    if (onClose) onClose();
    // Slight delay so dialog close animation finishes before cart opens
    setTimeout(() => buyNow(), 220);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-0 bg-black/85 border border-[color:var(--goldenelia-gold)]/15 hover:border-[color:var(--goldenelia-gold)]/45 transition-colors duration-500"
    >
      {/* Image */}
      <div
        className="relative aspect-square sm:aspect-auto overflow-hidden"
        style={{
          backgroundImage: `url('${product.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={`product-image-${product.id}`}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h4 className="font-serif-luxury text-xl sm:text-2xl text-[color:var(--goldenelia-cream)] leading-tight">
              {product.name}
            </h4>
            <p className="font-display text-[10px] tracking-[0.3em] text-[color:var(--goldenelia-gold)]/80 uppercase mt-1">
              {product.size}
            </p>
          </div>
          <p
            data-testid={`product-price-${product.id}`}
            className="gold-gradient-text font-serif-luxury text-2xl sm:text-3xl leading-none whitespace-nowrap pt-1"
          >
            €{product.price}
          </p>
        </div>

        <p
          data-testid={`product-story-${product.id}`}
          className="font-body text-[13px] text-[color:var(--goldenelia-cream)]/65 leading-relaxed mb-4"
        >
          {product.story}
        </p>

        {/* Contents */}
        <div className="mb-5">
          <p className="font-display text-[9px] tracking-[0.35em] text-[color:var(--goldenelia-gold)]/70 uppercase mb-2">
            {t("cretan.contents")}
          </p>
          <ul data-testid={`product-contents-${product.id}`} className="space-y-1.5">
            {product.contents.map((c, i) => (
              <li key={i} className="font-body text-[12px] text-[color:var(--goldenelia-cream)]/75 flex items-start gap-2">
                <span className="text-[color:var(--goldenelia-gold)] mt-0.5 leading-none">·</span>
                <span className="leading-snug">{c}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <QtyStepper
              value={qty}
              onChange={setQty}
              testid={`product-qty-${product.id}`}
            />
            <button
              type="button"
              onClick={handleAdd}
              data-testid={`product-add-${product.id}`}
              className="btn-ghost-gold !py-2.5 !px-4 text-[10px] flex items-center gap-2 flex-1 justify-center"
            >
              <Check size={12} strokeWidth={2} />
              {t("cart.addToCart")}
            </button>
          </div>
          <button
            type="button"
            onClick={handleBuyNow}
            data-testid={`product-buynow-${product.id}`}
            className="btn-solid-gold !py-2.5 !px-4 text-[10px] w-full"
          >
            {t("cart.buyNow")} →
          </button>
        </div>
      </div>
    </article>
  );
};

export const CategoryDialog = ({ open, onOpenChange, categoryKey }) => {
  const { t } = useLang();
  const videoRef = useRef(null);

  useEffect(() => {
    if (open && videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      } catch { /* noop */ }
    }
  }, [open, categoryKey]);

  if (!categoryKey) return null;
  const data = CATALOG[categoryKey];
  if (!data) return null;

  const name = t(`cretan.cats.${categoryKey}.name`);
  const desc = t(`cretan.cats.${categoryKey}.desc`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="category-dialog"
        className="max-w-4xl bg-black border-[color:var(--goldenelia-gold)]/40 text-[color:var(--goldenelia-cream)] p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        {/* Cinematic header — image only for AI-generated categories, video for others */}
        <div className="relative w-full overflow-hidden" style={{ height: "220px" }}>
          {(["oil", "honey", "raki", "vinegar", "giftbox"].includes(categoryKey)) ? (
            <div
              data-testid="category-dialog-image"
              className="absolute inset-0 bg-cover bg-center animate-[kenburns_18s_ease-in-out_infinite_alternate]"
              style={{ backgroundImage: `url('/cats/${categoryKey}.jpg')` }}
            />
          ) : (
            <video
              ref={videoRef}
              data-testid="category-dialog-video"
              className="absolute inset-0 w-full h-full object-cover"
              src={`/cats/${categoryKey}.mp4`}
              poster={`/cats/${categoryKey}.jpg`}
              muted
              loop
              playsInline
              autoPlay
              preload="auto"
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.95) 100%)",
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
            <p
              data-testid="category-dialog-eyebrow"
              className="font-display text-[10px] tracking-[0.5em] text-[color:var(--goldenelia-gold)] uppercase mb-2"
            >
              {t("cretan.eyebrow")}
            </p>
            <DialogTitle
              data-testid="category-dialog-title"
              className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text leading-tight italic"
            >
              {name}
            </DialogTitle>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <p className="font-body text-sm text-[color:var(--goldenelia-cream)]/65 leading-relaxed text-center mb-8">
            {desc}
          </p>

          <div className="space-y-4">
            {data.products.map((p) => (
              <ProductCard
                key={p.id}
                categoryKey={categoryKey}
                categoryName={name}
                product={p}
                onClose={() => onOpenChange(false)}
              />
            ))}
          </div>

          <p
            data-testid="category-dialog-note"
            className="font-body text-xs text-[color:var(--goldenelia-cream)]/45 mt-8 tracking-wider leading-relaxed text-center"
          >
            {t("cretan.dialogNote")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryDialog;
